/* eslint-disable */
import React, { useState, useEffect } from 'react';
import * as Store from '@apply/store';
import DataGrid, { Column, ColumnFixing } from 'devextreme-react/data-grid';
import { alertModal } from '@apply/ui';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit } from '@fortawesome/free-regular-svg-icons';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import LoadPanel from 'devextreme-react/load-panel';
import { BusinessAndPeopleApi } from '@apply/api';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { EntityOwnershipType } from '@apply/constants';
import { EditBorrowerOwnerPeopleGroupModal } from './modals/EditBorrowerOwnerPeopleGroupModal';
import { EditBorrowerOwnerEntityModal } from './modals/EditBorrowerOwnerEntityModal';
import { EditBorrowerOwnerPersonModal } from './modals/EditBorrowerOwnerPersonModal';

interface IBorrowerEntityPanelParameters {
    applicationInsights: ApplicationInsights;
    currentTopLevelBusiness: number;
}

const BorrowerEntityOwnerPanel = ({
    applicationInsights,
    currentTopLevelBusiness,
}: IBorrowerEntityPanelParameters): JSX.Element => {
    const businessAndPeopleApi = new BusinessAndPeopleApi.BusinessAndPeopleApi(applicationInsights);
    const [borrowerEntities, set_borrowerEntities] = useState([]);
    const [borrowerEntityId, set_borrowerEntityId] = useState(0);
    const [borrowerEntityOwners, set_borrowerEntityOwners] = useState([]);
    const [selectedBusinessId, set_selectedBusinessId] = useState(currentTopLevelBusiness);

    const [borrowerEntityOwnerPeopleGroupOpen, set_borrowerEntityOwnerPeopleGroupOpen] = useState(false);
    const [selectedPeopleGroupId, set_selectedPeopleGroupId] = useState(0);

    const [editOwnerBorrowerEntityOpen, set_borrowerEntityOwnerEntityOpen] = useState(false);
    const [selectedEntityOwnerId, set_selectedEntityOwnerId] = useState(0);

    const [borrowerEntityOwnerPersonOpen, set_borrowerEntityOwnerPersonOpen] = useState(false);
    const [selectedPersonOwnerId, set_selectedPersonOwnerId] = useState(0);

    const [loadingMessage, set_loadingMessage] = useState('');
    const [loadingOpen, set_loadingOpen] = useState(false);

    useEffect(() => {
        loadBorrowerEntities();
    }, [selectedBusinessId]);

    useEffect(() => {
        loadBorrowerEntityOwners();
    }, [borrowerEntityId]);

    useEffect(() => {
        if (borrowerEntities.length > 0) {
            set_borrowerEntityId(borrowerEntities[0].entityId);
        }
    }, [borrowerEntities]);

    useEffect(() => {
        set_selectedBusinessId(currentTopLevelBusiness);
    }, [currentTopLevelBusiness]);

    const loadBorrowerEntities = async () => {
        if (selectedBusinessId || selectedBusinessId >= 0) {
            try {
                set_loadingOpen(true);
                set_loadingMessage('Loading...');
                const response = await businessAndPeopleApi.getBorrowerEntities(selectedBusinessId);
                if (response.statusCode !== 200) {
                    set_borrowerEntities([]);
                    alertModal({
                        message: 'Failed to load Borrower Entities. Please try again.',
                    });
                } else {
                    set_borrowerEntities(response.result);
                }
            } catch (ex) {
                set_borrowerEntities([]);
                alertModal({
                    message: 'Failed to load Borrower Entities. Please try again.',
                });
            } finally {
                set_loadingOpen(false);
                set_loadingMessage('');
            }
        }
    };

    const loadBorrowerEntityOwners = async () => {
        if ((selectedBusinessId && borrowerEntityId) || (selectedBusinessId && borrowerEntityId > 0)) {
            try {
                set_loadingOpen(true);
                set_loadingMessage('Loading...');
                const response = await businessAndPeopleApi.getBorrowerEntityOwners(borrowerEntityId);
                if (response.statusCode !== 200) {
                    set_borrowerEntityOwners([]);
                    alertModal({
                        message: 'Failed to load Borrower Entity Owners. Please try again.',
                    });
                } else {
                    set_borrowerEntityOwners(response.result);
                }
            } catch (ex) {
                set_borrowerEntityOwners([]);
                alertModal({
                    message: 'Failed to load Borrower Entity Owners. Please try again.',
                });
            } finally {
                set_loadingOpen(false);
                set_loadingMessage('');
            }
        } else {
            set_borrowerEntityOwners([]);
        }
    };

    const onBorrowerOwnershipTypeCellRender = (e) => {
        if (e && e.value) {
            switch (e.value) {
                case EntityOwnershipType.Entity:
                    return 'Borrower Entity';
                case EntityOwnershipType.Principal:
                    return 'Person';
                case EntityOwnershipType.PeopleGroup:
                    return 'People Group';
                default:
                    return '';
            }
        }
    };

    const onPercentOwnershipRender = (e) => {
        if (e.value) {
            return e.value + '%';
        }
    };

    const renderBorrowerEntityOwnerCount = () => {
        let totalCount = 0;
        if (borrowerEntityOwners && borrowerEntityOwners.length > 0) {
            totalCount = borrowerEntityOwners.length;
        }
        return (
            <div>
                <span>Owners:</span> {totalCount}
            </div>
        );
    };

    const renderBorrowerEntityTotalOwnership = () => {
        let totalOwnershipPercentage = 0;
        if (borrowerEntityOwners && borrowerEntityOwners.length > 0) {
            totalOwnershipPercentage = borrowerEntityOwners.reduce((a, v) => (a = a + v.percentOwnership), 0);
        }
        return (
            <div>
                <span>Total Percent Ownership:</span> {totalOwnershipPercentage}%
            </div>
        );
    };

    const onActionRender = (e) => {
        return (
            <div>
                <button
                    className='button gridActionColumnButton'
                    title='Edit Owner'
                    onClick={(event) => onClickEditOwner(event, e.data)}
                    disabled={
                        e.data.ownershipType == EntityOwnershipType.Entity ||
                        e.data.ownershipType === EntityOwnershipType.Principal
                    }
                >
                    <FontAwesomeIcon icon={faEdit} />
                </button>
            </div>
        );
    };

    const onSelectedBorrowerEntityChange = (e) => {
        if (borrowerEntityId != e.target.value) {
            set_borrowerEntityId(e.target.value);
        }
    };

    const onEntityOwnerModalClose = async () => {
        set_borrowerEntityOwnerEntityOpen(false);
        set_selectedEntityOwnerId(0);
        loadBorrowerEntityOwners();
    };

    const onPeopleGroupModalClose = async () => {
        set_borrowerEntityOwnerPeopleGroupOpen(false);
        set_selectedPeopleGroupId(0);
        loadBorrowerEntityOwners();
    };
    const onPersonModalClose = async () => {
        set_borrowerEntityOwnerPersonOpen(false);
        set_selectedPersonOwnerId(0);
        loadBorrowerEntityOwners();
    };

    const onClickEditOwner = (event, data) => {
        event.preventDefault();
        event.stopPropagation();

        switch (data.ownershipType) {
            case EntityOwnershipType.Entity:
                set_selectedEntityOwnerId(data.ownerEntityId);
                set_borrowerEntityOwnerEntityOpen(true);
                return;
            case EntityOwnershipType.Principal:
                set_selectedPersonOwnerId(data.ownerPersonId);
                set_borrowerEntityOwnerPersonOpen(true);
                return;
            case EntityOwnershipType.PeopleGroup:
                set_selectedPeopleGroupId(data.ownerPeopleGroupId);
                set_borrowerEntityOwnerPeopleGroupOpen(true);
        }
    };

    const onClickAddBorrowerEntityOwner = async (id: number) => {
        set_selectedEntityOwnerId(id);
        set_borrowerEntityOwnerEntityOpen(true);
    };

    const onClickAddPersonOwner = async (id: number) => {
        set_selectedPersonOwnerId(id);
        set_borrowerEntityOwnerPersonOpen(true);
    };

    const onClickAddPeopleGroupOwner = async (id: number) => {
        set_selectedPeopleGroupId(id);
        set_borrowerEntityOwnerPeopleGroupOpen(true);
    };

    return (
        <>
            <div style={{ width: '100%', display: 'inline-block' }}>
                {!borrowerEntities ||
                    (borrowerEntities.length === 0 && (
                        <div
                            style={{
                                paddingBottom: '10px',
                                color: 'red',
                                width: '100%',
                            }}
                        >
                            <FontAwesomeIcon
                                icon={faExclamationTriangle}
                                style={{ paddingRight: '5px' }}
                            />
                            Please provide at least one Borrower Entity.
                        </div>
                    ))}
                {borrowerEntities && borrowerEntities.length > 0 && (
                    <>
                        <div style={{ display: 'flex', paddingTop: '10px' }}>
                            <label
                                style={{
                                    paddingTop: '2px',
                                    paddingRight: '10px',
                                }}
                                htmlFor='userBusinesses'
                            >
                                Borrower:
                            </label>
                            <select
                                id={'userBorrowerEntities'}
                                value={borrowerEntityId}
                                onChange={onSelectedBorrowerEntityChange}
                                required={true}
                                disabled={false}
                            >
                                {borrowerEntities.map((v, i) => {
                                    return (
                                        <option
                                            key={i}
                                            value={v.entityId}
                                        >
                                            {v.name}
                                        </option>
                                    );
                                })}
                            </select>
                        </div>
                        <div
                            style={{
                                width: '100%',
                                paddingTop: '30px',
                                paddingBottom: '10px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <button
                                className='button'
                                onClick={(event) => {
                                    onClickAddBorrowerEntityOwner(0);
                                }}
                                disabled={false}
                                style={{
                                    display: 'block',
                                    marginRight: '15px',
                                    width: '33%',
                                }}
                            >
                                + Entity
                            </button>
                            <button
                                className='button'
                                onClick={(event) => {
                                    onClickAddPersonOwner(0);
                                }}
                                style={{
                                    display: 'block',
                                    marginRight: '15px',
                                    width: '33%',
                                }}
                            >
                                + Person
                            </button>
                            <button
                                className='button'
                                onClick={(event) => {
                                    onClickAddPeopleGroupOwner(0);
                                }}
                                disabled={false}
                                style={{ display: 'block', width: '33%' }}
                            >
                                + Group of People
                            </button>
                        </div>
                        <div
                            style={{
                                width: '100%',
                                display: 'flex',
                                paddingBottom: '5px',
                                fontSize: 'small',
                            }}
                        >
                            <div style={{ width: '50%', textAlign: 'left' }}>{renderBorrowerEntityOwnerCount()}</div>
                            <div style={{ width: '50%', textAlign: 'right' }}>{renderBorrowerEntityTotalOwnership()}</div>
                        </div>
                        <div style={{ width: '100%' }}>
                            <DataGrid
                                keyExpr='entityId'
                                id='borrowerEntitiesOwnerTable'
                                dataSource={borrowerEntityOwners}
                                showBorders={true}
                                showRowLines={true}
                                remoteOperations={false}
                                allowColumnResizing={true}
                                allowColumnReordering={false}
                                columnResizingMode={'nextColumn'}
                                columnMinWidth={20}
                                cellHintEnabled={true}
                                width={'auto'}
                                noDataText='No Owners found'
                            >
                                <ColumnFixing enabled={true} />
                                <Column
                                    caption='Type'
                                    dataField='ownershipType'
                                    alignment={'left'}
                                    minWidth={50}
                                    allowSorting={false}
                                    cellRender={(typeId) => onBorrowerOwnershipTypeCellRender(typeId)}
                                />
                                <Column
                                    dataField='ownerName'
                                    caption='Owner Name'
                                    alignment='left'
                                    minWidth={250}
                                    allowSorting={false}
                                />
                                <Column
                                    dataField='percentOwnership'
                                    caption='Percent of Ownership'
                                    alignment='left'
                                    cellRender={onPercentOwnershipRender}
                                    minWidth={100}
                                    allowSorting={false}
                                />
                                <Column
                                    caption=''
                                    cellRender={onActionRender}
                                    width={80}
                                    alignment='center'
                                />
                            </DataGrid>
                        </div>
                    </>
                )}
            </div>
            <LoadPanel
                hideOnOutsideClick={false}
                visible={loadingOpen}
                message={loadingMessage}
            />
            <EditBorrowerOwnerPeopleGroupModal
                selectedBusinessId={selectedBusinessId}
                borrowerEntityId={borrowerEntityId}
                applicationInsights={applicationInsights}
                isOpen={borrowerEntityOwnerPeopleGroupOpen}
                onCloseCallback={onPeopleGroupModalClose}
                peopleGroupId={selectedPeopleGroupId}
            />
            <EditBorrowerOwnerPersonModal
                selectedBusinessId={selectedBusinessId}
                borrowerEntityId={borrowerEntityId}
                applicationInsights={applicationInsights}
                isOpen={borrowerEntityOwnerPersonOpen}
                onCloseCallback={onPersonModalClose}
                personId={selectedPersonOwnerId}
            />
            <EditBorrowerOwnerEntityModal
                selectedBusinessId={selectedBusinessId}
                borrowerEntityId={borrowerEntityId}
                applicationInsights={applicationInsights}
                isOpen={editOwnerBorrowerEntityOpen}
                onCloseCallback={onEntityOwnerModalClose}
                entityOwnerId={selectedEntityOwnerId}
            />
        </>
    );
};

export default BorrowerEntityOwnerPanel;
